/*
Copyright 2026 ВИН СОЛЮШЕНС

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { Config } from "../config/Config";

/**
 * Служебные участники звонка — боты, а не люди.
 *
 * CTO 14.09.2026: «протоколист и другие боты в ВКС режиме должны иметь плитку
 * в 10% от стандарта». Бот сидит в звонке без камеры и без звука, но занимает
 * в сетке столько же места, сколько человек: на встрече из четверых это четверть
 * экрана под чёрный прямоугольник с надписью.
 *
 * Список проверен по базе сервера 14.09.2026 (`select name from users`) — это
 * существующие учётки, а не выдуманные. Кто из них приходит в звонок сегодня,
 * видно по журналу LiveKit: за двое суток из служебных встречался только
 * `@service_account` (протоколист заходит именно им). Остальные перечислены на
 * будущее: их появление в звонке не должно потребовать новой сборки.
 *
 * Список можно переопределить в настройках звонилки (`service_user_ids`) —
 * иначе на другом сервере пришлось бы править код.
 */
const DEFAULT_SERVICE_LOCALPARTS = [
  "service_account",
  "bot_proto",
  "protocolbot",
  "protocol.bot",
  "meetingbot",
  "meetingbot2",
  "ecop_bot",
  "azure_devbot",
  "etl_bot",
  "notification_bot",
  "concierge",
  "concierge_next",
];

/** Локальная часть адреса: «@service_account:matrix…» → «service_account». */
function localpart(userId: string): string {
  const withoutSigil = userId.startsWith("@") ? userId.slice(1) : userId;
  const colon = withoutSigil.indexOf(":");
  return (
    colon === -1 ? withoutSigil : withoutSigil.slice(0, colon)
  ).toLowerCase();
}

/**
 * Служебный ли это участник.
 *
 * Сравниваем по локальной части, а не по полному адресу: один и тот же бот на
 * стенде и на рабочем сервере живёт под разными доменами, и список из полных
 * адресов пришлось бы держать в двух видах.
 */
export function isServiceUser(userId: string | undefined): boolean {
  if (!userId) return false;

  const configured = Config.get().service_user_ids;
  const names = (
    configured?.length ? configured : DEFAULT_SERVICE_LOCALPARTS
  ).map(localpart);
  return names.includes(localpart(userId));
}

/**
 * Во сколько раз площадь плитки бота меньше обычной.
 *
 * «Плитка в 10% от стандарта» — это про занимаемое место, поэтому десятая доля
 * берётся от ПЛОЩАДИ: сторона выходит в корень из десяти раз меньше, примерно
 * треть. Если бы десятую долю взяли от стороны, плитка стала бы точкой в сотую
 * долю площади, и имени в ней бы не поместилось.
 */
export const SERVICE_TILE_AREA = 0.1;

/** Во сколько раз короче сторона такой плитки. */
export const SERVICE_TILE_SCALE = Math.sqrt(SERVICE_TILE_AREA);
